import React, { useEffect, useMemo, useState } from "react"
import { useGetAdminDashboard } from "../../hooks/AdminDashboard/useGetAdminDashboard"
import { AdminDashboard } from "../../Objects/AdminDashboard/AdminDashboard"
import { usePayment } from "../../hooks/Payment/usePayment"
import { AdminTutorialSection } from "./Tutorials/AdminTutorialSection"
import { useGetAdminRuntimeStats } from "../../hooks/AdminDashboard/useGetAdminRuntimeStats"
import { AdminRuntimeStats } from "../../Objects/AdminDashboard/AdminRuntimeStats"
import { useGetAdminFinancialMetrics } from "../../hooks/AdminDashboard/useGetAdminFinancialMetrics"
import { AdminFinancialMetrics } from "../../Objects/AdminDashboard/AdminFinancialMetrics"
import { OverviewSection } from "./sections/OverviewSection"
import { ServerRuntimeSection } from "./sections/ServerRuntimeSection"
import { CommercialPerformanceSection } from "./sections/CommercialPerformanceSection"
import { SubscriptionsSection } from "./sections/SubscriptionsSection"
import { FinancialMetricsSection } from "./sections/FinancialMetricsSection"
import { StoreFinancialHealthSection } from "./sections/StoreFinancialHealthSection"
import { useGetAdminLogs } from "../../hooks/AdminDashboard/useGetAdminLogs"
import { AdminLogs } from "../../Objects/AdminDashboard/AdminLogs"
import { ServerLogsSection } from "./sections/ServerLogsSection"
import { useGetAdminSubscriptionMonitoringDetails } from "../../hooks/AdminDashboard/useGetAdminSubscriptionMonitoringDetails"
import { AdminSubscriptionMonitoringDetails } from "../../Objects/AdminDashboard/AdminSubscriptionMonitoringDetails"
import { SubscriptionMonitoringDetailsPopup } from "./SubscriptionMonitoringDetailsPopup"
import { useAdminLandingLead } from "../../hooks/LandingLead/useAdminLandingLead"
import { AdminLandingLead } from "../../Objects/LandingLead/AdminLandingLead"
import { LandingLeadsSection } from "./sections/LandingLeadsSection"
import { useAdminGrowthInvestment } from "../../hooks/AdminDashboard/useAdminGrowthInvestment"
import { AdminGrowthInvestmentDashboard } from "../../Objects/AdminGrowthInvestment/AdminGrowthInvestmentDashboard"
import { AdminGrowthInvestmentFilter } from "../../Objects/AdminGrowthInvestment/AdminGrowthInvestmentFilter"
import { GrowthInvestmentsSection } from "./sections/GrowthInvestmentsSection"
import { getStoreNow } from "../../utils/timeZone"
import { AdminSubscriptionCase } from "../../Objects/AdminDashboard/AdminSubscriptionCase"
import { AdminSubscriptionEditPopup } from "./AdminSubscriptionEditPopup"
import { MercadoPagoQueriesSection } from "./sections/MercadoPagoQueriesSection"
import { PlatformWhatsAppSection } from "./sections/PlatformWhatsAppSection"
import { PlatformNoticesSection } from "./sections/PlatformNoticesSection"
import { PlansSection } from "./sections/PlansSection"
import { PlanCouponsSection } from "./sections/PlanCouponsSection"
import { ResellersSection } from "./sections/ResellersSection"
import { ResellerCommissionPlansSection } from "./sections/ResellerCommissionPlansSection"
import { AdminEmployeesPage } from "./AdminStaff/AdminEmployeesPage"

export type AdminDashboardSection =
  | "overview"
  | "subscriptions"
  | "commercial_performance"
  | "tutorials"
  | "financial_metrics"
  | "store_financial_health"
  | "server_runtime"
  | "server_logs"
  | "landing_leads"
  | "growth_investments"
  | "mercado_pago_queries"
  | "platform_whatsapp"
  | "platform_notices"
  | "plans"
  | "plan_coupons"
  | "resellers"
  | "reseller_commission_plans"
  | "admin_staff"

interface AdminDashboardPageProps {
  section: AdminDashboardSection
}

export const AdminDashboardPage: React.FC<AdminDashboardPageProps> = ({ section }) => {
  const { handleGetAdminDashboard } = useGetAdminDashboard()

  const { handleGetAdminRuntimeStats } = useGetAdminRuntimeStats()

  const { handleGetAdminLogs, handleDeleteAdminLog, handleDownloadAdminLog, isDeletingLog, isDownloadingLog } =
    useGetAdminLogs()

  const { handleGetAdminFinancialMetrics } = useGetAdminFinancialMetrics()

  const { handleGetAdminSubscriptionMonitoringDetails, handleToggleCompanyTestOrDemo, handleUpdateSubscription } =
    useGetAdminSubscriptionMonitoringDetails()

  const { manualActivateSubscription } = usePayment()

  const { handleGetAll: handleGetAllLandingLeads, handleUpdateContactStatus } = useAdminLandingLead()

  const { handleGetDashboard: handleGetGrowthInvestmentDashboard } = useAdminGrowthInvestment()

  const [dashboard, setDashboard] = useState(new AdminDashboard())

  const [financialMetrics, setFinancialMetrics] = useState(new AdminFinancialMetrics())

  const [runtimeStats, setRuntimeStats] = useState(new AdminRuntimeStats())

  const [logs, setLogs] = useState(new AdminLogs())

  const [isSubscriptionMonitoringPopupOpen, setIsSubscriptionMonitoringPopupOpen] = useState(false)

  const [landingLeads, setLandingLeads] = useState<AdminLandingLead[]>([])

  const [growthInvestmentDashboard, setGrowthInvestmentDashboard] = useState(new AdminGrowthInvestmentDashboard())

  const [growthInvestmentToday] = useState(() => getStoreNow())

  const [growthInvestmentFilter, setGrowthInvestmentFilter] = useState(
    new AdminGrowthInvestmentFilter({
      startDate: new Date(growthInvestmentToday.getFullYear(), growthInvestmentToday.getMonth(), 1).toInputDate(),
      endDate: growthInvestmentToday.toInputDate(),
    }),
  )

  const [subscriptionMonitoringDetails, setSubscriptionMonitoringDetails] = useState(
    new AdminSubscriptionMonitoringDetails(),
  )

  const [editingSubscription, setEditingSubscription] = useState<AdminSubscriptionCase | null>(null)

  useEffect(() => {
    if (section === "overview" || section === "subscriptions" || section === "commercial_performance") {
      handleGetAdminDashboard(setDashboard)
    }

    if (section === "server_runtime") {
      handleGetAdminRuntimeStats(setRuntimeStats)

      const intervalId = window.setInterval(() => {
        handleGetAdminRuntimeStats(setRuntimeStats, true)
      }, 5000)

      return () => window.clearInterval(intervalId)
    }

    if (section === "server_logs") {
      handleGetAdminLogs(setLogs)

      const intervalId = window.setInterval(() => {
        handleGetAdminLogs(setLogs, true)
      }, 15000)

      return () => window.clearInterval(intervalId)
    }

    if (section === "financial_metrics" || section === "store_financial_health") {
      handleGetAdminFinancialMetrics(setFinancialMetrics)
    }

    if (section === "landing_leads") {
      handleGetAllLandingLeads(setLandingLeads)
    }

    if (section === "growth_investments") {
      handleGetGrowthInvestmentDashboard(growthInvestmentFilter, setGrowthInvestmentDashboard)
    }
  }, [section])

  const originSeries = useMemo(
    () =>
      dashboard.commercialPerformance.origins.map((item) => ({
        label: item.originType.toECommercialOriginTypeString(),
        value: item.subscriptionsCount,
      })),
    [dashboard.commercialPerformance.origins],
  )

  const originRevenueSeries = useMemo(
    () =>
      dashboard.commercialPerformance.origins.map((item) => ({
        label: item.originType.toECommercialOriginTypeString(),
        value: item.totalRevenue,
      })),
    [dashboard.commercialPerformance.origins],
  )

  const monthlyOrdersAmountSeries = useMemo(
    () =>
      financialMetrics.monthlySeries.map((item) => ({
        label: item.label,
        value: item.ordersAmount,
      })),
    [financialMetrics.monthlySeries],
  )

  const monthlyPlatformFeeSeries = useMemo(
    () =>
      financialMetrics.monthlySeries.map((item) => ({
        label: item.label,
        value: item.platformFeeAmount,
      })),
    [financialMetrics.monthlySeries],
  )

  const monthlyManualExternalOrdersSeries = useMemo(
    () =>
      financialMetrics.monthlySeries.map((item) => ({
        label: item.label,
        value: item.manualExternalOrdersNetAmount,
      })),
    [financialMetrics.monthlySeries],
  )

  if (section === "overview") {
    return <OverviewSection dashboard={dashboard} />
  }

  if (section === "server_runtime") {
    return <ServerRuntimeSection runtimeStats={runtimeStats} />
  }

  if (section === "tutorials") {
    return <AdminTutorialSection />
  }

  if (section === "server_logs") {
    return (
      <ServerLogsSection
        logs={logs}
        onRefresh={() => handleGetAdminLogs(setLogs)}
        onDelete={(relativePath) => handleDeleteAdminLog(relativePath, () => handleGetAdminLogs(setLogs))}
        onDownload={handleDownloadAdminLog}
        isDeleting={isDeletingLog}
        isDownloading={isDownloadingLog}
      />
    )
  }

  if (section === "mercado_pago_queries") {
    return <MercadoPagoQueriesSection />
  }

  if (section === "platform_whatsapp") {
    return <PlatformWhatsAppSection />
  }

  if (section === "platform_notices") {
    return <PlatformNoticesSection />
  }

  if (section === "plans") {
    return <PlansSection />
  }

  if (section === "plan_coupons") {
    return <PlanCouponsSection />
  }

  if (section === "resellers") {
    return <ResellersSection />
  }

  if (section === "reseller_commission_plans") {
    return <ResellerCommissionPlansSection />
  }

  if (section === "commercial_performance") {
    return (
      <CommercialPerformanceSection
        dashboard={dashboard}
        originSeries={originSeries}
        originRevenueSeries={originRevenueSeries}
      />
    )
  }

  if (section === "subscriptions") {
    return (
      <>
        <SubscriptionsSection
          dashboard={dashboard}
          onManualActivate={(subscriptionId) => {
            manualActivateSubscription(subscriptionId, () => {
              handleGetAdminDashboard(setDashboard)
            })
          }}
          onShowMoreOptions={(companyId) => {
            handleGetAdminSubscriptionMonitoringDetails(companyId, (data) => {
              setSubscriptionMonitoringDetails(data)
              setIsSubscriptionMonitoringPopupOpen(true)
            })
          }}
          onEditSubscription={setEditingSubscription}
          onRefresh={() => handleGetAdminDashboard(setDashboard)}
        />

        {editingSubscription && (
          <AdminSubscriptionEditPopup
            subscription={editingSubscription}
            onClose={() => setEditingSubscription(null)}
            onSave={(subscriptionId, request) => {
              handleUpdateSubscription(subscriptionId, request, () => {
                setEditingSubscription(null)
                handleGetAdminDashboard(setDashboard)
              })
            }}
          />
        )}

        {isSubscriptionMonitoringPopupOpen && (
          <SubscriptionMonitoringDetailsPopup
            details={subscriptionMonitoringDetails}
            onClose={() => setIsSubscriptionMonitoringPopupOpen(false)}
            onToggleTestOrDemo={(value) => {
              handleToggleCompanyTestOrDemo(subscriptionMonitoringDetails.companyId, value, () => {
                handleGetAdminSubscriptionMonitoringDetails(
                  subscriptionMonitoringDetails.companyId,
                  setSubscriptionMonitoringDetails,
                )
                handleGetAdminDashboard(setDashboard)
              })
            }}
          />
        )}
      </>
    )
  }

  if (section === "financial_metrics") {
    return (
      <FinancialMetricsSection
        financialMetrics={financialMetrics}
        monthlyOrdersAmountSeries={monthlyOrdersAmountSeries}
        monthlyPlatformFeeSeries={monthlyPlatformFeeSeries}
        monthlyManualExternalOrdersSeries={monthlyManualExternalOrdersSeries}
      />
    )
  }

  if (section === "store_financial_health") {
    return (
      <StoreFinancialHealthSection
        financialMetrics={financialMetrics}
        onRefresh={() => handleGetAdminFinancialMetrics(setFinancialMetrics)}
      />
    )
  }

  if (section === "landing_leads") {
    return (
      <LandingLeadsSection
        leads={landingLeads}
        onToggle={(id, value) => handleUpdateContactStatus(id, value, () => handleGetAllLandingLeads(setLandingLeads))}
        onRefresh={() => handleGetAllLandingLeads(setLandingLeads)}
      />
    )
  }

  if (section === "growth_investments") {
    return (
      <GrowthInvestmentsSection
        dashboard={growthInvestmentDashboard}
        filter={growthInvestmentFilter}
        onFilterChange={(nextFilter) => {
          setGrowthInvestmentFilter(nextFilter)
          handleGetGrowthInvestmentDashboard(nextFilter, setGrowthInvestmentDashboard)
        }}
        onRefresh={() => handleGetGrowthInvestmentDashboard(growthInvestmentFilter, setGrowthInvestmentDashboard)}
      />
    )
  }

  if (section === "admin_staff") {
    return <AdminEmployeesPage />
  }

  return <OverviewSection dashboard={dashboard} />
}
